import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
    AutoSelection,
    Button,
    Card,
    Checkbox,
    Combobox,
    Divider,
    Icon,
    Layout,
    LegacyStack,
    Listbox,
    Tag,
    Text,
    TextContainer
} from "@shopify/polaris";
import {SearchIcon} from '@shopify/polaris-icons';
import {currencyListOptions} from "../../helper/currencyList";
import CurrencyPreview from "../CurrencyPreview";

const AppConfiguration = ({formik, changeTextFn}) => {
    const deselectedOptions = useMemo(
        () => currencyListOptions,
        [],
    );
    const [selectedOptions, setSelectedOptions] = useState(formik?.values?.currenciesList?.map(currency => currency?.value));
    const [currencyInputValue, setCurrencyInputValue] = useState('');
    const [currencyOptions, setCurrencyOptions] = useState(deselectedOptions);
    const defaultCurrencyDeselectedOptions = useMemo(
        () => formik?.values?.currenciesList?.length ? formik.values.currenciesList : deselectedOptions,
        [formik?.values?.currenciesList, deselectedOptions]
    );
    const [defaultCurrencyOptions, setDefaultCurrencyOptions] = useState(defaultCurrencyDeselectedOptions);
    const [defaultCurrencyInputValue, setDefaultCurrencyInputValue] = useState(formik?.values?.defaultCurrency?.label || '');
    const [selectedDefaultCurrency, setSelectedDefaultCurrency] = useState(formik?.values?.defaultCurrency?.value || '');


    const escapeSpecialRegExCharacters = useCallback(
        (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
        [],
    );


    useEffect(() => {
        if (formik?.values?.currenciesList?.length) {
            setSelectedOptions(formik?.values?.currenciesList?.map(currency => currency?.value))
        } else {
            setSelectedOptions([])
        }
    }, [formik?.values?.currenciesList])

    useEffect(() => {
        if (formik?.values?.defaultCurrency) {
            setSelectedDefaultCurrency(formik.values.defaultCurrency.value);
            setDefaultCurrencyInputValue(formik.values.defaultCurrency.label);
        }
    }, [formik?.values?.defaultCurrency]);

    const changeCurriencesText = useCallback(
        (value) => {
            setCurrencyInputValue(value);
            if (value === '') {
                setCurrencyOptions(deselectedOptions);
                return;
            }

            const filterRegex = new RegExp(escapeSpecialRegExCharacters(value), 'i');
            const resultOptions = deselectedOptions.filter((option) =>
                option.label.match(filterRegex),
            );
            setCurrencyOptions(resultOptions);
        },
        [deselectedOptions, escapeSpecialRegExCharacters],
    )


    const updateCurrencySelection = useCallback((selected) => {
            let setData = [];
            if (selectedOptions.includes(selected)) {
                setData = selectedOptions.filter((option) => option !== selected);
                setSelectedOptions(setData);
            } else {
                setData = [...selectedOptions, selected];
                setSelectedOptions(setData);
            }
            changeCurriencesText('');
            changeTextFn('currenciesList', setData)
        },
        [selectedOptions, changeCurriencesText],
    );

    const removeCurrecnyTag = useCallback((tag) => () => {
            const options = [...selectedOptions];
            options.splice(options.indexOf(tag), 1);
            setSelectedOptions(options);
            changeTextFn('currenciesList', options)
        },
        [selectedOptions],
    );

    const tagsCurrencyMarkup = selectedOptions.map((option) => {
        let currencyData = currencyListOptions.find(country => country.value === option)
        return (
            <Tag key={`option-${option}`} onRemove={removeCurrecnyTag(option)}>
                {currencyData?.label}
            </Tag>
        )
    });


    const optionsCurrencyMarkup = currencyOptions.length > 0
        ? currencyOptions.map((option) => {
            const {label, value} = option;

            return (
                <Listbox.Option
                    key={`${value}`}
                    value={value}
                    selected={selectedOptions.includes(value)}
                    accessibilityLabel={label}
                >
                    {label}
                </Listbox.Option>
            );
        })
        : null;

    useEffect(() => {
        if (formik?.values?.currenciesList?.length) {
            setSelectedOptions(formik.values.currenciesList.map(currency => currency.value));
            setDefaultCurrencyOptions(formik.values.currenciesList);
            
            if (!formik.values.defaultCurrency) {
                const firstCurrency = formik.values.currenciesList[0];
                setSelectedDefaultCurrency(firstCurrency.value);
                setDefaultCurrencyInputValue(firstCurrency.label);
                changeTextFn('defaultCurrency', firstCurrency);
            }
        } else {
            setSelectedOptions([]);
            setDefaultCurrencyOptions(deselectedOptions);
        }
    }, [formik?.values?.currenciesList, deselectedOptions]);

    const updateDefaultCurrencyText = useCallback(
        (value) => {
            setDefaultCurrencyInputValue(value);

            if (value === '') {
                setDefaultCurrencyOptions(defaultCurrencyDeselectedOptions);
                return;
            }

            const filterRegex = new RegExp(escapeSpecialRegExCharacters(value), 'i');
            const resultOptions = defaultCurrencyDeselectedOptions.filter((option) =>
                option.label.match(filterRegex),
            );
            setDefaultCurrencyOptions(resultOptions);
        },
        [defaultCurrencyDeselectedOptions, escapeSpecialRegExCharacters],
    );

    const defaultCurrencyOptionsMarkup =
        defaultCurrencyOptions.length > 0
            ? defaultCurrencyOptions.map((option) => {
                const {label, value} = option;
                return (
                    <Listbox.Option
                        key={`${value}`}
                        value={value}
                        selected={selectedDefaultCurrency === value}
                        accessibilityLabel={label}
                    >
                        {label}
                    </Listbox.Option>
                );
            })
            : null;

    const updateDefaultCurrencySelection = useCallback(
        (selected) => {
            const matchedOption = defaultCurrencyDeselectedOptions.find((option) => {
                return option.value === selected;
            });
            setSelectedDefaultCurrency(selected);
            setDefaultCurrencyInputValue((matchedOption && matchedOption.label) || '');
            changeTextFn('defaultCurrency', matchedOption)
        },
        [defaultCurrencyDeselectedOptions],
    );
    return (
        <>
            <div className="mt-20">
                <Layout>
                    <Layout.AnnotatedSection
                        id="application-state"
                        title="Application state"
                        description="Control whether the currency converter is active in your store's theme editor. When enabled, customers can use the converter while browsing your store."
                    >
                        <Card>
                            <div className="d-flex justify-content-space-between align-items-center">
                                <Text variant="span">
                                    The app is <span
                                    style={{fontWeight: 'bold'}}>{formik?.values?.appStatus ? 'Enabled' : 'Disabled'}</span> in
                                    your store.
                                </Text>
                                <Button
                                    loading={formik.values?.appStatusLoader}
                                    variant="primary"
                                    onClick={() => {
                                        changeTextFn('appStatus', !formik.values?.appStatus)
                                    }}
                                    size="large"
                                    tone={formik.values.appStatus ? "critical" : ''}
                                >
                                    {formik.values.appStatus ? 'Disable' : 'Enable'}

                                </Button>
                            </div>
                        </Card>
                    </Layout.AnnotatedSection>
                </Layout>
            </div>
            <div className="mt-20">
                <Divider borderColor="border"/>
            </div>
            <div className="mt-20">
                <Layout.AnnotatedSection
                    id="currency-settings"
                    title="Currency Settings"
                    description={<>
                    <Text as={'span'}>Control whether the currency converter is active in your store's theme editor. When enabled, customers can use the converter while browsing your store.</Text>
                    <CurrencyPreview
                        priceFormat={formik.values?.priceFormat || 0}
                        displayFlag={formik.values?.displayFlag || 0}
                        flagStyle={formik.values?.flagStyle || 0}
                        displayBorder={formik.values?.displayBorder || 0}
                        borderColor={formik.values?.borderLineColor || '#d8d8d8'}
                        backgroundColor={formik.values?.backgroundColor || '#ffffff'}
                        backgroundHoverColor={formik.values?.hoverBackgroundColor || '#f6f6f7'}
                        textColor={formik?.values?.textColor || '#202223'}
                        textHoverColor={formik?.values?.textHoverColor || '#008060'}
                        selectedCurrencies={formik?.values?.currenciesList}
                        currencies={currencyListOptions}
                        removeDecimals={formik?.values?.removeDecimals}
                                priceRounding={formik?.values?.pricingRound}
                    />
                    </>}
                >
                    <Card>
                        <div className="currency-settings-dropdown">
                            <Text variant="span" fontWeight='semibold'>Select Currencies</Text>
                            <Combobox
                                allowMultiple
                                activator={
                                    <Combobox.TextField
                                        prefix={<Icon source={SearchIcon}/>}
                                        onChange={changeCurriencesText}
                                        label="Search currencies"
                                        labelHidden
                                        value={currencyInputValue}
                                        placeholder="Search currencies"
                                        autoComplete="off"
                                    />
                                }
                            >
                                {optionsCurrencyMarkup ? (
                                    <Listbox
                                        autoSelection={AutoSelection.None}
                                        onSelect={updateCurrencySelection}
                                    >
                                        {optionsCurrencyMarkup}
                                    </Listbox>
                                ) : null}
                            </Combobox>
                            <TextContainer>
                                <LegacyStack>{tagsCurrencyMarkup}</LegacyStack>
                            </TextContainer>
                        </div>

                        <div className="mt-20 currency-settings-dropdown">
                            <Text variant="span" fontWeight='semibold'>My Default Currency</Text>
                            <Combobox
                                activator={
                                    <Combobox.TextField
                                        prefix={<Icon source={SearchIcon}/>}
                                        onChange={updateDefaultCurrencyText}
                                        label="Default Currency"
                                        labelHidden
                                        value={defaultCurrencyInputValue}
                                        placeholder="Search default currency"
                                        autoComplete="off"
                                    />
                                }
                            >
                                {defaultCurrencyOptionsMarkup ? (
                                    <Listbox onSelect={updateDefaultCurrencySelection}>
                                        {defaultCurrencyOptionsMarkup}
                                    </Listbox>
                                ) : null}
                            </Combobox>
                        </div>

                        {/* Apply Market Compatibile Mode */}
                        <div className="mt-20 currency-settings-dropdown">
                            <Text variant="span" fontWeight='semibold'>Apply Market Compatibile Mode</Text>
                            <Checkbox
                                label="Apply Market Compatibile Mode"
                                checked={formik.values.marketCompatibileMode}
                                onChange={() => changeTextFn('marketCompatibileMode', !formik.values.marketCompatibileMode)}
                                helpText="When enabled, prices, currency, taxes, and discounts update automatically based on the selected market using Shopify Markets. The page refreshes when the market changes to apply the correct pricing."
                            />
                        </div>

                    </Card>
                </Layout.AnnotatedSection>
            </div>
        </>
    );
};

export default AppConfiguration;
